import { nanoid } from 'nanoid';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AnimationClip,
  AnimationMixer,
  LoopOnce,
  LoopRepeat,
  Object3D,
} from 'three';

import { FrameRate } from '@cartwhl/common/types';

import { useSyncedFrameMixers } from './useSyncedFrameMixers';

/**
 * @typedef {Object} Config
 * @property {FrameRate} [frameRate] - The frame rate of the animation (default: 24).
 * @property {number} [playbackSpeed] - The playback speed of the animation (default: 1).
 * @property {boolean} [loop] - Whether the animation should loop (default: true).
 * @property {boolean} [autoplay] - Whether the animation should autoplay (default: true).
 * @property {boolean} [disabled] - Whether the mixer should be disabled (default: false).
 * @property {function} [onReady] - Callback when the animation is ready.
 * @property {function} [onFrameUpdate] - Callback on frame update.
 * @property {function} [onLoopFinish] - Callback when the loop finishes.
 * @property {function} [onPlayChange] - Callback when the play state changes.
 * @property {ReturnType<typeof useSyncedFrameMixers>} [syncController] - Used for syncing multiple mixers, will turn off autoplay.
 */
type Config = {
  frameRate?: FrameRate;
  playbackSpeed?: number;
  frameStepSize?: number;
  loop?: boolean;
  autoplay?: boolean;
  disabled?: boolean;
  onReady?: (duration: { seconds: number; frames: number }) => void;
  onFrameUpdate?: (frame: number) => void;
  onLoopFinish?: () => void;
  onPlayChange?: (isPlaying: boolean) => void;
  syncController?: ReturnType<typeof useSyncedFrameMixers>;
};

/**
 * Custom hook to manage an animation mixer.
 *
 * @param {AnimationClip} clip - The animation clip to be played.
 * @param {Object3D<THREE.Event>} root - The root object of the animation.
 * @param {Config} [config] - Configuration options for the mixer.
 * @returns {Object} An object containing the mixer controls and status.
 * @returns {string} return.id - Unique identifier for the mixer instance.
 * @returns {function} return.update - Function to update the mixer.
 * @returns {function} return.play - Function to play the animation.
 * @returns {function} return.setFrame - Function to set the current frame of the animation.
 * @returns {function} return.getFrame - Function to get the current frame of the animation.
 * @returns {Object} return.duration - The duration of the animation in seconds and frames.
 * @returns {function} return.getPlayStatus - Function to get the play status of the animation.
 */
export default function useFrameMixer(
  clip: AnimationClip,
  root?: Object3D<THREE.Event>,
  {
    frameRate = 24,
    playbackSpeed = 1,
    frameStepSize = 1,
    loop = true,
    autoplay = true,
    disabled = false,
    onReady,
    onFrameUpdate,
    onLoopFinish,
    onPlayChange,
    syncController,
  }: Config = {},
) {
  const FRAME_TIME = 1 / frameRate; // Time per frame in seconds

  const id = useRef<string>(nanoid());
  const mixer = useRef<AnimationMixer>(new AnimationMixer(new Object3D()));
  const accumulator = useRef(0); // Accumulated time since last frame
  const [duration, setDuration] = useState({ seconds: 0, frames: 0 });

  const previousPlayStatus = useRef<boolean | undefined>(undefined);
  const activeAction = useRef<THREE.AnimationAction | undefined>(null);

  const updateMixer = () => {
    if (!root) return;

    // Remember the play state, so we can restore it after updating the mixer
    if (mixer.current) {
      previousPlayStatus.current = getPlayStatus();
    }

    const action = mixer.current.clipAction(clip, root);
    action.setLoop(loop ? LoopRepeat : LoopOnce, Infinity);

    if (loop === false || syncController) {
      action.clampWhenFinished = true;
    }

    // We start the animation paused (Otherwise the character shows up in a T-pose)
    mixer.current.timeScale = 1;

    if (activeAction.current) {
      // NOTE: Spitting the mixer reset to a different useEffect
      // there is no longer any popping, so there is no need to
      // use crossFade. Leave it here incase we need crossFade
      // in the future.
      // if (crossFade) {
      //   console.log('CrossFade');
      //   activeAction.current.setEffectiveWeight(1);
      //   activeAction.current.setEffectiveTimeScale(1);
      //   activeAction.current.enabled = true;
      //   activeAction.current.clampWhenFinished = true;

      //   action.setEffectiveWeight(1);
      //   action.setEffectiveTimeScale(1);
      //   action.time = 0;
      //   action.enabled = true;
      //   action.clampWhenFinished = true;

      //   activeAction.current.crossFadeTo(action, 1, false);
      // } else {

      // need to stop previous action so the next action can play correctly
      activeAction.current.stop();

      // }
    }

    action.play();
    mixer.current.setTime(mixer.current.time); // Setting the time here prevents it from showing a t-pose

    // Restore the play state from previous mixer instance
    mixer.current.timeScale = previousPlayStatus.current ? 1 : 0;

    // If autoplay is enabled, start the animation
    // Unless a syncController is provided
    // Or unless we have a previous play status
    if (
      autoplay &&
      !syncController &&
      previousPlayStatus.current === undefined
    ) {
      mixer.current.timeScale = 1;
      action.play();
    }

    if (onPlayChange) {
      onPlayChange(mixer.current.timeScale > 0);
    }

    const seconds = clip.duration;
    const frames = Math.ceil(seconds * frameRate);
    const myDuration = { seconds, frames };
    setDuration(myDuration);

    if (syncController) {
      syncController.register(id.current, {
        play,
        setFrame,
        duration: myDuration,
        getPlayStatus,
      });
    }

    if (onReady) {
      onReady(myDuration);
    }

    const handleLoopFinish = () => {
      if (onLoopFinish) {
        onLoopFinish();
      }
      if (syncController) {
        syncController?.onLoopFinish(id.current);
      }
    };

    mixer.current.addEventListener('loop', handleLoopFinish);
    activeAction.current = action;

    return () => {
      mixer.current.removeEventListener('loop', handleLoopFinish);
      if (onPlayChange) {
        onPlayChange(false);
      }
    };
  };

  // initialize the mixer
  useEffect(() => {
    return () => {
      mixer.current.stopAllAction();
      if (root) {
        mixer.current.uncacheRoot(root);
      }
      activeAction.current = null;
    };
  }, [root]);

  // initialize the clip
  // eslint-disabled-next-line react-hooks/exhaustive-deps
  useEffect(updateMixer, [
    root,
    clip,
    loop,
    disabled,
    autoplay,
    frameRate,
    syncController,
    frameStepSize,
  ]);

  // update the mixer
  const update = (delta: number) => {
    // Skip updates when disabled
    if (disabled) return;

    accumulator.current += delta * playbackSpeed;

    // Process updates in fixed time steps
    while (accumulator.current >= FRAME_TIME * frameStepSize) {
      mixer.current.update(FRAME_TIME * frameStepSize);
      accumulator.current -= FRAME_TIME * frameStepSize;
    }
    if (onFrameUpdate && mixer.current.timeScale > 0) {
      onFrameUpdate(getFrame());
    }
  };

  const play = (isPlaying: boolean) => {
    if (!mixer.current) return;

    // Don't allow play changes when disabled
    if (disabled) return;

    if (isPlaying) {
      mixer.current.timeScale = 1;
    } else {
      mixer.current.timeScale = 0;
    }
    if (onPlayChange) {
      onPlayChange(isPlaying);
    }
  };

  const setFrame = (newFrame: number) => {
    if (!mixer.current) return;
    const frame =
      ((newFrame % duration.frames) + duration.frames) % duration.frames;

    const steppedFrame = Math.floor(frame / frameStepSize) * frameStepSize;

    const action = mixer.current.clipAction(clip);
    const time = steppedFrame * FRAME_TIME;
    const originalTimeScale = mixer.current.timeScale;

    // Temporarily set timeScale to 1 to ensure frame update
    mixer.current.timeScale = 1;
    action.time = time;
    mixer.current.setTime(time);

    // Restore original timeScale
    mixer.current.timeScale = originalTimeScale;

    if (onFrameUpdate) {
      onFrameUpdate(getFrame());
    }
  };

  const getFrame = () => {
    if (!mixer.current) return 0;

    const currentFrame = Math.round(
      (mixer.current.time % duration.seconds) / FRAME_TIME,
    );
    return currentFrame || 0;
  };

  const getPlayStatus = () => mixer.current.timeScale !== 0;

  return useMemo(
    () => ({
      id: id.current,
      update,
      play,
      setFrame,
      getFrame,
      duration,
      getPlayStatus,
    }),
    [duration, playbackSpeed, frameStepSize],
  );
}
